/**
 * NotificationItem component
 * Displays a single notification with icon, title, time, and actions
 */

import { memo } from 'react'
import { X, Bell, AlertTriangle, CheckCircle, Info, FileText, Archive, Target, Lock, Unlock } from 'lucide-react'
import type { Notification } from '../../dashboard/types/dashboard.types'

interface NotificationItemProps {
  notification: Notification
  onMarkRead: (id: string) => void
  onNavigate?: (notification: Notification) => void
}

// Map notification types to icons
const notificationIcons = {
  deadline: AlertTriangle,
  escalation: AlertTriangle,
  approval_request: CheckCircle,
  system: Info,
  filing_deadline: FileText,
  dormancy: Archive,
  milestone_advance: Target,
  milestone_blocked: Lock,
  gate_satisfied: Unlock
}

// Get relative time string
function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export const NotificationItem = memo(function NotificationItem({ notification, onMarkRead, onNavigate }: NotificationItemProps) {
  const Icon = notificationIcons[notification.type] || Bell
  const relativeTime = getRelativeTime(notification.created_at)

  const handleClick = () => {
    if (onNavigate) {
      onNavigate(notification)
    }
  }

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    onMarkRead(notification.id)
  }

  return (
    <div
      className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
      onClick={handleClick}
    >
      <div className={`notification-icon ${notification.type}`}>
        <Icon size={18} />
      </div>

      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-title">{notification.title}</span>
          <span className="notification-time">{relativeTime}</span>
        </div>
        {notification.message && (
          <p className="notification-message">{notification.message}</p>
        )}
      </div>

      <button
        className="notification-dismiss"
        onClick={handleMarkRead}
        title="Mark as read"
        aria-label="Mark as read"
      >
        <X size={16} />
      </button>
    </div>
  )
})
